/**
 * margine — quanto incassiamo e quanto ci costa uno strumento a canone di un'azienda nel periodo di fatturazione
 * (atlante cap. 452). Simone, 30/10/2026: «ci dobbiamo sempre assicurare che ci sia un grande margine».
 *
 * L'incasso viene dal listino e dal consumo (`RigaDiConsumo`): il canone più l'extra, solo se arrivano davvero in
 * fattura (nell'abbonamento, fuori prova, non regalato). Il costo dai costi veri che il server registra in
 * `platform_costi_veri` (AI di ogni risposta + canale riletto dal fornitore), in dollari, convertiti col cambio del
 * registro dei costi. Solo per chi gestisce la piattaforma: i nostri costi non si mostrano mai a un cliente.
 *
 * Pura: niente Firestore. I livelli sono chiavi (ASCII, mai tradotte); le etichette le mette chi mostra.
 */
import { extraInFattura, type RigaDiConsumo } from './consumoDellAzienda';

export const COLLEZIONE_COSTI_VERI = 'platform_costi_veri';

/** Sopra questa quota dell'incasso il margine è «ampio»; sotto quella più bassa è «stretto». */
export const MARGINE_AMPIO = 0.7;
export const MARGINE_MINIMO = 0.3;

export const LIVELLI_DEL_MARGINE = ['ampio', 'da_guardare', 'stretto', 'in_perdita', 'senza_incasso'] as const;
export type LivelloDelMargine = (typeof LIVELLI_DEL_MARGINE)[number];

/** I costi veri di uno strumento in un periodo, letti dal documento del server (dati non fidati). */
export interface CostiVeriDelloStrumento {
  readonly ai_usd: number;
  readonly ai_risposte: number;
  /** Il banco di prova: costa, ma non serve un cliente. */
  readonly ai_prova_usd: number;
  /** Il canale come lo fattura il fornitore; null = non ancora riletto. */
  readonly canale_usd: number | null;
  /** Qualche risposta contata al prezzo prudente (un modello fuori listino): il listino va completato. */
  readonly ai_prezzo_prudente: boolean;
}

const numero = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);

/** Pura: dal documento `platform_costi_veri/{azienda}__{periodo}` ai costi di uno strumento (null = niente registrato). */
export function costiVeriDaDati(dati: unknown, capability: string): CostiVeriDelloStrumento | null {
  const strumenti = dati && typeof dati === 'object' ? (dati as { strumenti?: unknown }).strumenti : null;
  const s = strumenti && typeof strumenti === 'object' ? (strumenti as Record<string, unknown>)[capability] : null;
  if (!s || typeof s !== 'object') return null;
  const v = s as Record<string, unknown>;
  return {
    ai_usd: numero(v.ai_usd),
    ai_risposte: numero(v.ai_risposte),
    ai_prova_usd: numero(v.ai_prova_usd),
    canale_usd: typeof v.canale_usd === 'number' && Number.isFinite(v.canale_usd) ? Math.max(0, v.canale_usd) : null,
    ai_prezzo_prudente: v.ai_prezzo_prudente === true,
  };
}

/** Pura: quanto arriva in fattura nel periodo, in centesimi: canone più extra, solo se l'azienda lo paga davvero. */
export function incassoDelPeriodoCentesimi(r: RigaDiConsumo): number {
  if (!extraInFattura(r)) return 0;
  return Math.round(r.strumento.canone_mensile_eur * 100) + r.extra_centesimi;
}

export interface MargineDelPeriodo {
  readonly incasso_centesimi: number;
  /** Tutto ciò che ci costa, prova compresa, in centesimi di euro (per eccesso). */
  readonly costo_centesimi: number;
  readonly margine_centesimi: number;
  /** Il margine sull'incasso (0,85 = 85%); null senza incasso. */
  readonly quota: number | null;
  readonly livello: LivelloDelMargine;
}

/** Pura: il margine del periodo, col cambio dollaro → euro del registro dei costi. */
export function margineDelPeriodo(r: RigaDiConsumo, costi: CostiVeriDelloStrumento, eurPerUsd: number): MargineDelPeriodo {
  const usd = costi.ai_usd + costi.ai_prova_usd + (costi.canale_usd ?? 0);
  const costo = Math.ceil(usd * eurPerUsd * 100);
  const incasso = incassoDelPeriodoCentesimi(r);
  const margine = incasso - costo;
  if (incasso === 0) return { incasso_centesimi: 0, costo_centesimi: costo, margine_centesimi: -costo, quota: null, livello: 'senza_incasso' };
  const quota = margine / incasso;
  const livello: LivelloDelMargine = margine < 0 ? 'in_perdita' : quota < MARGINE_MINIMO ? 'stretto' : quota < MARGINE_AMPIO ? 'da_guardare' : 'ampio';
  return { incasso_centesimi: incasso, costo_centesimi: costo, margine_centesimi: margine, quota, livello };
}
